import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import { 
  ExclamationTriangleIcon,
  ShoppingBagIcon,
  UsersIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const DeleteProductConfirmation = ({ 
  isOpen, 
  onClose, 
  product, 
  onProductDeleted 
}) => {
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [deletionInfo, setDeletionInfo] = useState(null);
  const [canDelete, setCanDelete] = useState(true);
  const [deleteReason, setDeleteReason] = useState('');

  // Check if product can be safely deleted
  useEffect(() => {
    if (isOpen && product) {
      validateDeletion();
    }
  }, [isOpen, product]);

  const validateDeletion = async () => {
    setValidating(true);
    
    try {
      const response = await axios.get(`/api/products/${product.id}/deletion-check`);

      setDeletionInfo(response.data);
      setCanDelete(response.data.canDelete);
      
    } catch (error) {
      console.error('Error validating product deletion:', error);
      // Default to allowing deletion if validation fails
      setCanDelete(true);
      setDeletionInfo({
        canDelete: true,
        totalOrders: 0,
        activeOrders: 0,
        totalRevenue: 0,
        totalCustomers: 0,
        warnings: []
      });
    } finally {
      setValidating(false);
    }
  };

  const handleDelete = async () => {
    if (!product || !canDelete) return;

    setLoading(true);

    try {
      // Include deletion reason if provided
      const payload = deleteReason ? { reason: deleteReason } : {};
      
      await axios.delete(`/api/products/${product.id}`, { 
        data: payload 
      });

      // Success notification
      toast.success(`המוצר "${product.name}" נמחק בהצלחה`);
      
      // Notify parent component
      if (onProductDeleted) {
        onProductDeleted(product.id);
      }
      
      // Close modal
      onClose();
      
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה במחיקת המוצר. אנא נסה שנית.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !validating) {
      setDeleteReason('');
      onClose();
    }
  };

  if (!product) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="אישור מחיקת מוצר"
      size="md"
      preventClose={loading || validating}
    >
      <div className="space-y-6">
        {validating ? (
          // Validation Loading State
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">בודק אפשרות מחיקה...</p>
          </div>
        ) : (
          <>
            {/* Warning Header */}
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-full ${
                canDelete ? 'bg-yellow-100' : 'bg-red-100'
              }`}>
                <ExclamationTriangleIcon className={`h-6 w-6 ${
                  canDelete ? 'text-yellow-600' : 'text-red-600'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {canDelete ? 'אישור מחיקת מוצר' : 'לא ניתן למחוק מוצר'}
                </h3>
                <p className="text-sm text-gray-600">
                  {product.name} (SKU: {product.sku})
                </p>
              </div>
            </div>

            {/* Product Impact Information */}
            {deletionInfo && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">השפעת המחיקה:</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <ShoppingBagIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {deletionInfo?.totalOrders || 0}
                      </p>
                      <p className="text-xs text-gray-500">הזמנות כוללות</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <UsersIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {deletionInfo?.totalCustomers || 0}
                      </p>
                      <p className="text-xs text-gray-500">לקוחות</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        ₪{(deletionInfo?.totalRevenue || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">סך הכנסות</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                    <div>
                      <p className="text-sm font-medium text-red-900">
                        {deletionInfo?.activeOrders || 0}
                      </p>
                      <p className="text-xs text-gray-500">הזמנות פעילות</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Warnings */}
            {deletionInfo?.warnings && deletionInfo.warnings.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-900 mb-2">אזהרות:</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  {deletionInfo.warnings.map((warning, index) => (
                    <li key={index} className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-red-400 rounded-full ml-2"></span>
                      {warning}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Cannot Delete Message */}
            {!canDelete && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700">
                  <strong>לא ניתן למחוק את המוצר</strong> מכיוון שיש לו הזמנות פעילות או תלויות אחרות במערכת.
                </p>
                <p className="text-sm text-red-600 mt-2">
                  <strong>אפשרויות חלופיות:</strong>
                </p>
                <ul className="text-sm text-red-600 mt-1 space-y-1">
                  <li>• שנה את סטטוס המוצר ל"לא פעיל"</li>
                  <li>• הסתר את המוצר מהקטלוג</li>
                  <li>• חכה עד שכל ההזמנות הפעילות יושלמו</li>
                </ul>
              </div>
            )}

            {/* Deletion Reason (if can delete) */}
            {canDelete && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  סיבת המחיקה (אופציונלי)
                </label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="הזן סיבה למחיקת המוצר לצורכי ביקורת..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Confirmation Message */}
            {canDelete && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>שים לב:</strong> מחיקת המוצר היא פעולה בלתי הפיכה. כל המידע הקשור למוצר יימחק לצמיתות.
                </p>
              </div>
            )}
          </>
        )}

        {/* Action Buttons */}
        {!validating && (
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-outline"
              disabled={loading}
            >
              ביטול
            </button>
            
            {canDelete ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={loading}
                className="btn bg-red-600 text-white hover:bg-red-700 flex items-center"
              >
                {loading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2" />
                )}
                מחק מוצר
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  // Could redirect to edit product to deactivate instead
                  alert('במקום מחיקה, שקול לשנות את סטטוס המוצר ל"לא פעיל"');
                  handleClose();
                }}
                className="btn bg-blue-600 text-white hover:bg-blue-700"
              >
                השבת מוצר
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DeleteProductConfirmation;
